import { UnreachableError } from 'base/helpers';
import { COLOR_TOKENS } from 'base/ui/tokens';
import {
  BlocksService,
  Comment,
  CommentThread,
  ServerMessage,
  UserAvatar,
} from 'blocks/blocks_service';
import {
  CommentAnchoringState,
  CommentAnchoringViewport,
  Project,
} from 'blocks/editor/model';
import {
  deleteThreadFromProject,
  findThreadsInProject,
} from 'blocks/editor/model_comment_helpers';
import {
  idForSelectionState,
  SelectionState,
} from 'blocks/editor/selection/states';
import { UndoManager } from 'blocks/woot_woot/undo';
import { computed, makeObservable, observable } from 'mobx';
import { PresenceTracker } from './presence_tracker';

type AttachToPosition = {
  selection: SelectionState;
  x: number;
  y: number;
};

export class CommentsStore {
  private readonly threadsById = observable.map<number, CommentThread>([], {
    deep: false,
  });
  private readonly commentsById = observable.map<number, Comment>([], {
    deep: false,
  });
  // We store read state in a separate map, so it can be updated instantly locally
  private readonly commentReadById = observable.map<number, boolean>([], {
    deep: false,
  });

  constructor(
    private readonly blocksService: BlocksService,
    private readonly undo: UndoManager,
    private readonly project: Project,
    private readonly projectToken: string,
    private readonly presence: PresenceTracker,
  ) {
    makeObservable(this);
  }

  handleMessage(
    msg: ServerMessage & { type: 'init' | 'commentThread' | 'comment' },
  ) {
    switch (msg.type) {
      case 'init':
        for (const thread of msg.initialCommentThreads) {
          this.addThread(thread);
        }
        for (const comment of msg.initialComments) {
          this.addComment(comment);
        }
        break;
      case 'commentThread':
        this.addThread(msg.thread);
        break;
      case 'comment':
        this.addComment(msg.comment);
        this.addThread(msg.thread);
        break;
      default:
        throw new UnreachableError(msg);
    }
  }

  private addThread(thread: CommentThread) {
    const cur = this.threadsById.get(thread.id);
    if (cur && cur.version > thread.version) {
      return;
    }
    this.threadsById.set(thread.id, thread);
  }

  private addComment(comment: Comment) {
    const cur = this.commentsById.get(comment.id);
    if (cur && cur.version > comment.version) {
      return;
    }
    this.commentsById.set(comment.id, comment);
  }

  async submitComment(
    content: string,
    opts:
      | {
          type: 'toplevel';
          viewport: CommentAnchoringViewport;
          attachTo: AttachToPosition[];
        }
      | { type: 'reply'; threadId: number },
  ) {
    if (opts.type === 'toplevel' && opts.attachTo.length === 0) {
      throw new Error('Can not add new comment without attachTo');
    }
    const res = await this.blocksService.projectSubmitComment({
      projectToken: this.projectToken,
      thread: opts.type === 'reply' ? opts.threadId : { new: true },
      content,
    });
    this.commentReadById.set(res.comment.id, true);
    if (opts.type === 'toplevel') {
      this.undo.doPermanently(() => {
        for (const { selection, x, y } of opts.attachTo) {
          attachCommentThread(
            res.thread.id.toString(),
            selection,
            this.project,
            { x, y, viewport: opts.viewport },
          );
        }
      });
    }
    this.handleMessage(res);
    return res;
  }

  async updateThread(
    threadId: number,
    opts: { resolved?: boolean; deleted?: boolean },
  ) {
    const res = await this.blocksService.projectUpdateCommentThread({
      projectToken: this.projectToken,
      threadId,
      ...opts,
    });
    this.handleMessage(res);
  }

  canDeleteThread(threadId: number) {
    const comments = this.commentsByThread.get(threadId);
    const firstComment = comments?.[0];
    if (!firstComment) return false;
    return firstComment.author.id === this.myAvatar?.id;
  }

  canEditOrDeleteComment(commentId: number) {
    const comment = this.commentsById.get(commentId);
    if (!comment) return false;
    if (comment.author.id !== this.myAvatar?.id) return false;
    const allComments = this.commentsByThread.get(comment.threadId);
    if (allComments?.[0].id === commentId) {
      return false; // don't allow deleting the 1st comment - they can delete the thread instead
    }
    return true;
  }

  async updateComment(
    commentId: number,
    opts: { content?: string; deleted?: boolean },
  ) {
    const res = await this.blocksService.projectUpdateComment({
      projectToken: this.projectToken,
      commentId,
      ...opts,
    });
    this.handleMessage(res);
    return res.comment;
  }

  async setCommentRead(commentId: number, isRead: boolean) {
    if (this.commentReadById.get(commentId) === isRead) {
      return;
    }
    this.commentReadById.set(commentId, isRead);
    await this.blocksService.projectReadComment({
      projectToken: this.projectToken,
      commentId,
      isRead,
    });
  }

  isCommentRead(commentId: number) {
    return (
      this.commentReadById.get(commentId) ??
      this.commentsById.get(commentId)?.isRead ??
      false
    );
  }

  isThreadRead(threadId: number) {
    return this.getCommentsInThread(threadId).every((c) =>
      this.isCommentRead(c.id),
    );
  }

  private get threadToSelection() {
    return findThreadsInProject(this.project);
  }

  getThreadSelectionAndAnchoring(threadId: number) {
    const selAndAnchor = this.threadToSelection.get(threadId.toString());
    if (!selAndAnchor) {
      // Thread has no anchor
      return undefined;
    }
    const { sel, anchoring } = selAndAnchor;
    return { sel, selectionId: idForSelectionState(sel), anchoring };
  }

  @computed
  get allThreads() {
    return [...this.threadsById.values()]
      .sort((a, b) => b.id - a.id)
      .filter(
        (thread) =>
          this.commentsByThread.has(thread.id) &&
          !thread.deleted &&
          this.threadToSelection.has(thread.id.toString()),
      );
  }

  getThread(id: number) {
    return this.threadsById.get(id);
  }

  @computed
  private get commentsByThread() {
    const result = new Map<number, Comment[]>();
    for (const comment of this.commentsById.values()) {
      if (comment.deleted) continue;
      const comments = result.get(comment.threadId) ?? [];
      comments.push(comment);
      result.set(comment.threadId, comments);
    }
    for (const comments of result.values()) {
      comments.sort((a, b) => a.id - b.id);
    }
    return result;
  }

  readonly getCommentsInThread = (threadId: number) => {
    return this.commentsByThread.get(threadId) ?? [];
  };

  @computed
  get myAvatar(): UserAvatar {
    return this.presence.mySession?.user ?? FALLBACK_AVATAR;
  }
}

const FALLBACK_AVATAR: UserAvatar = {
  id: -1,
  firstName: '?',
  lastName: '',
  color: COLOR_TOKENS.colorRed,
};

function attachCommentThread(
  threadId: string,
  selection: SelectionState,
  project: Project,
  anchoring: CommentAnchoringState,
) {
  if (selection.element) {
    selection.element.commentThreads.set(threadId, {
      ...anchoring,
      nodeId: selection.nodeId,
    });
  } else if (selection.section) {
    selection.section.commentThreads.set(threadId, anchoring);
  } else if (selection.page) {
    selection.page.commentThreads.set(threadId, anchoring);
  } else if (selection.space && selection.space !== 'home') {
    selection.space.commentThreads.set(threadId, anchoring);
  } else {
    project.commentThreads.set(threadId, anchoring);
  }
}

export function moveCommentThread(
  project: Project,
  threadId: string,
  attachTo: AttachToPosition[],
  viewport: CommentAnchoringViewport,
) {
  deleteThreadFromProject(project, threadId);
  for (const { selection, x, y } of attachTo) {
    attachCommentThread(threadId, selection, project, { x, y, viewport });
  }
}
